'use server'

import { apiRequest, ApiResponse } from '../request'

export type TransferStatus =
    | 'TRANSF_REQUESTED'
    | 'TRANSF_AUTHORIZED'
    | 'TRANSF_IN_PROGRESS'
    | 'TRANSF_COMPLETED'
    | 'TRANSF_CANCELLED'

export type TransferType = 'INTERNAL' | 'EXTERNAL_OUT' | 'EXTERNAL_IN'

export interface BedTransfer {
    id: number
    companyId: number
    hospitalId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
    }
    type: TransferType
    status: TransferStatus
    fromUnitId?: number
    fromUnit?: {
        id: number
        name: string
    }
    fromBedId?: number
    fromBed?: {
        id: number
        number: string
    }
    toUnitId?: number
    toUnit?: {
        id: number
        name: string
    }
    toBedId?: number
    toBed?: {
        id: number
        number: string
    }
    externalFacility?: string
    reason: string
    requestedAt: string
    requestedById?: number
    requestedBy?: {
        id: number
        name: string
    }
    authorizedAt?: string
    authorizedById?: number
    authorizedBy?: {
        id: number
        name: string
    }
    startedAt?: string
    completedAt?: string
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface RequestTransferData {
    admissionId: number
    type: TransferType
    toUnitId?: number
    toBedId?: number
    externalFacility?: string
    reason: string
    notes?: string
}

export interface AuthorizeTransferData {
    approved: boolean
    toBedId?: number
    notes?: string
}

export interface TransferFilters {
    hospitalId?: number
    status?: TransferStatus
    type?: TransferType
    unitId?: number
}

export async function getTransfers(
    companyId?: number,
    filters?: TransferFilters
): Promise<ApiResponse<BedTransfer[]>> {
    return apiRequest<BedTransfer[]>('hospital/transfers', {
        params: { companyId, ...filters },
    })
}

export async function getTransfer(id: number): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>(`hospital/transfers/${id}`, {})
}

export async function getPendingTransfers(
    companyId: number
): Promise<ApiResponse<BedTransfer[]>> {
    return apiRequest<BedTransfer[]>(`hospital/transfers/pending/${companyId}`, {})
}

export async function requestTransfer(
    data: RequestTransferData
): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>('hospital/transfers', { data }, 'post')
}

export async function authorizeTransfer(
    id: number,
    data: AuthorizeTransferData
): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>(
        `hospital/transfers/${id}/authorize`,
        { data },
        'patch'
    )
}

export async function startTransfer(
    id: number
): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>(`hospital/transfers/${id}/execute`, {}, 'patch')
}

export async function completeTransfer(
    id: number
): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>(`hospital/transfers/${id}/complete`, {}, 'patch')
}

export async function cancelTransfer(
    id: number,
    reason?: string
): Promise<ApiResponse<BedTransfer>> {
    return apiRequest<BedTransfer>(
        `hospital/transfers/${id}/cancel`,
        { params: { reason } },
        'patch'
    )
}

export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
    TRANSF_REQUESTED: 'Solicitada',
    TRANSF_AUTHORIZED: 'Autorizada',
    TRANSF_IN_PROGRESS: 'Em Transporte',
    TRANSF_COMPLETED: 'Concluída',
    TRANSF_CANCELLED: 'Cancelada',
}

export const TRANSFER_STATUS_COLORS: Record<TransferStatus, string> = {
    TRANSF_REQUESTED: 'badge-warning',
    TRANSF_AUTHORIZED: 'badge-info',
    TRANSF_IN_PROGRESS: 'badge-secondary',
    TRANSF_COMPLETED: 'badge-success',
    TRANSF_CANCELLED: 'badge-error',
}

export const TRANSFER_TYPE_LABELS: Record<TransferType, string> = {
    INTERNAL: 'Interna',
    EXTERNAL_OUT: 'Saída Externa',
    EXTERNAL_IN: 'Entrada Externa',
}
